import { useEffect, useState } from "react";
import { AlertCircle, RotateCcw, Play } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { extractErrorSummary } from "@/lib/utils/error-summary";
import { useReviewStore } from "@/stores/reviewStore";

interface RunErrorCardProps {
  runId: string;
  status: "failed" | "blocked";
}

export function RunErrorCard({ runId, status }: RunErrorCardProps) {
  const retryRun = useReviewStore((s) => s.retryRun);
  const resumeRun = useReviewStore((s) => s.resumeRun);
  const fetchRunLogLines = useReviewStore((s) => s.fetchRunLogLines);
  const [summary, setSummary] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let disposed = false;
    void fetchRunLogLines(runId).then((lines) => {
      if (!disposed) setSummary(extractErrorSummary(lines));
    });
    return () => {
      disposed = true;
    };
  }, [runId, fetchRunLogLines]);

  const act = async (fn: (id: string) => Promise<boolean>) => {
    setBusy(true);
    try {
      await fn(runId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-[var(--colony-red)]/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm text-[var(--colony-red)]">
          <AlertCircle className="h-4 w-4" />
          {status === "failed" ? "Run failed" : "Run blocked"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {summary === null ? (
          <div className="text-xs text-muted-foreground">Loading logs...</div>
        ) : summary.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            No logs available for this run.
          </div>
        ) : (
          <pre className="max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs leading-relaxed">
            {summary.join("\n")}
          </pre>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void act(resumeRun)}
          >
            <Play className="mr-1 h-3 w-3" />
            Resume from failed step
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void act(retryRun)}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Retry from start
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
